/* @flow */
import * as React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import styled, { css } from "styled-components";
import Media from "react-media";
import Gettext from "../common/Gettext";
import { rtl } from "../common/styled-mixins";
import { withDeposit } from "../common/withDeposit";
import { Link } from "../common/Link";
import { mapDispatchToFetch } from "../common/fetch";
import { getViewedStock } from "../../redux/reducer/tabs";
import withLanguage from "../withLocaleChange";
import Tabs from "../Tabs";
import Logo from "./Logo";
import Button from "./Button";
import { Wallet } from "../Icons/Tournaments";
import ToggleNav from "../Navigation/Hamburger";
import Navigation from "../Navigation/Navigation";
import AccountInHeader from "../AccountType/AccountInHeader";
import { useBonusTimerApi } from "../hooks/useBonusTimer";
import { StockInTools } from "../Toolbar/SwitchStock";
import { ModalConsumer, type ShowModal } from "../common/ModalProvider";
import { Provider as RatingExpanderProvider } from "../Tournaments/Account/Tournament/RatingExpander";
import { disableRefill } from "../Tournaments/helpers/refill";
import Tournament from "../Tournaments/Api";
import { RefillPopup } from "../Tournaments/Popups";

import { mapStateToTime } from "../Timers";
import _BonusTimerBadge from "../Icons/BonusTimer";
import PercentIcon from "../Icons/Percent";
import { TourContext } from "../Tour";
import ABTest from "../ABTest";

import type { State as GlobalState } from "../../redux/reducer";
import type { Dispatch } from "../../redux/action";

import MenuIcon from "@material-ui/icons/MenuOutlined";
import { useSuggestedPromoCode } from "../Bell/Labels";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 74px;
  padding-left: 20px;
  text-align: justify;
  color: ${({ theme }) => theme.tradeHeaderAccent};
  background-color: ${({ theme }) => theme.mainDark};
  background-image: ${({ theme }) =>
    `linear-gradient(to bottom, ${theme.tradeHeaderGradientTop} 0, ${
      theme.tradeHeaderGradientBottom
    } 100%)`};
  border-bottom: 1px solid ${({ theme }) => theme.mainSeparator};
  ${({ theme }) =>
    theme.isIntlProjectLight &&
    css`
      border-bottom: none;
    `};
  flex: 1;

  @media (max-width: 959px) {
    height: 48px;
    padding-left: 0;
  }
`;

const Section = styled.div`
  display: inline-block;
  vertical-align: middle;
  line-height: normal;

  ${props =>
    props.center &&
    css`
      flex-grow: 1;
      height: 73px;
      margin-right: auto;

      @media (max-width: 959px) {
        height: 47px;
      }
    `} ${props =>
    props.separate &&
    css`
      height: 73px;
      padding-right: 15px;

      @media (max-width: 959px) {
        height: 47px;
      }
    `};

  @media (max-width: 959px) {
    min-width: 0;
    display: flex;
    flex-flow: row nowrap;
    overflow: hidden;
  }
`;

const Table = styled.div`
  display: table;
  height: 100%;
  white-space: nowrap;

  @media (max-width: 959px) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    align-content: flex-end;
    justify-content: flex-end;
    flex: 1;
    min-width: 0;
  }
`;

const TableCell = styled.div`
  display: table-cell;
  vertical-align: middle;

  @media (max-width: 959px) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    align-content: flex-end;
    justify-content: flex-end;
    min-width: 0;
  }
`;

const BadgeContainer = styled.span`
  position: absolute;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  color: #1d2027;

  @media (max-width: 959px) {
    top: 50%;
    transform: translateY(-50%);
    right: -18px;
    width: 37px;
    height: 37px;
  }

  @media (min-width: 960px) {
    top: -6px;
    right: -9px;
    width: 25px;
    height: 25px;
  }
`;

const Layers = styled.i`
  position: absolute;
  top: 0;
  ${rtl("left", 0)};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  user-select: none;
  pointer-events: none;
`;

const BadgeBgContainer = styled(Layers)`
  flex-direction: column;
  align-content: flex-start;
  justify-content: flex-start;
  color: #ffcc00;
  z-index: 1;
`;

const StockLink = styled(Link)`
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: auto;
`;

const BonusTimerBadge = styled(_BonusTimerBadge)`
  flex-grow: 0;
  flex-shrink: 0;
`;

const TimerContainer = styled(Layers)`
  z-index: 2;
`;

const PercentContainer = styled(Layers)`
  z-index: 3;
`;

const DepositBonusButton = styled(Button)`
  text-decoration: none;
`;

type BonusBadgeProps = {
  timer?: React.Node
};

const DepositBonusBadge = (props: BonusBadgeProps) => (
  <Media query="(max-width: 959px)">
    {matches => (
      <BadgeContainer>
        <BadgeBgContainer>
          <BonusTimerBadge width={matches ? 37 : 25} />
        </BadgeBgContainer>
        {props.timer != null && matches ? (
          <TimerContainer>{props.timer}</TimerContainer>
        ) : null}
        <PercentContainer>
          <PercentIcon />
        </PercentContainer>
      </BadgeContainer>
    )}
  </Media>
);

type DepositButtonProps = {
  time: number,
  children: React.Node
};

function DepositButton({ promocode, children }) {
  if (promocode != null) {
    return (
      <DepositBonusButton
        as={Link}
        type="deposit"
        to={`/fillup?promocode=${promocode}`}
      >
        {children}
        <Media
          query="(min-width: 960px)"
          render={() => <DepositBonusBadge />}
        />
      </DepositBonusButton>
    );
  } else {
    return (
      <DepositBonusButton as={Link} type="deposit" to="/fillup">
        {children}
      </DepositBonusButton>
    );
  }
}

const DepositBonusBtn = connect(mapStateToTime)((props: DepositButtonProps) => {
  let suggestedPromoCode = useSuggestedPromoCode();

  return (
    <ABTest campaign="first_deposit_bonus">
      {(showTest: boolean) => {

        if (showTest === false) {
          return (
            <DepositButton promocode={suggestedPromoCode}>
              {props.children}
            </DepositButton>
          );
        }

        const bonus = useBonusTimerApi({ time: props.time });

        if (
          bonus == null ||
          bonus.duration < 0 ||
          bonus.status !== "available"
        ) {
          return (
            <DepositButton promocode={suggestedPromoCode}>
              {props.children}
            </DepositButton>
          );
        }

        return (
          <DepositButton promocode={bonus.promo}>
            {props.children}
          </DepositButton>
        );
      }}
    </ABTest>
  );
});

type Props = {
  activeStock: string,
  profile: Object,
  accounts: Array<Object>,
  minFillup: number,
  initialDeposit: number,
  isVIP: boolean,
  isIOS: boolean,
  dispatch: Dispatch,
  history: Object,
  location: Object,
  match: Object,
  deals: Object,
  showDeposit: boolean,
  appVersion: number,
  onLocaleChange: (locale: string) => void,
  fetch: (method: string, options?: Object) => Promise<Object>,
  onRefillDemoClick: () => void,
  onProfileUpdate: () => void
};

type State = {
  changeLocaleFetching: boolean,
  isNavActive: boolean
};

class Header extends React.PureComponent<Props, State> {
  static contextType = TourContext;

  state = {
    changeLocaleFetching: false,
    isNavActive: false
  };

  toggle = () => {
    this.setState({ isNavActive: !this.state.isNavActive });
  };

  // Use this method instead of `toggle`
  //  as ClickOutside click handler due to slow
  // unmount flow caused by Motion
  hide = () => {
    this.setState({ isNavActive: false });
  };

  onDepositClick = () => {
    this.props.history.push("/fillup");
  };

  render() {
    const {
      profile,
      accounts,
      isVIP,
      showDeposit,
      isAndroidApp,
      isIOS,
      appVersion
    } = this.props;

    const activeAccount = accounts.find(
      account => account.id === profile.active_account_id
    );

    if (!activeAccount) {
      return null;
    }

    if (isAndroidApp || (isIOS && appVersion >= 2000000)) return null;

    const openDealsSum = this.props.deals.reduce(
      (res, item) => (item.close_timestamp == null ? res + item.amount : res),
      0
    );

    const showRefillLoader =
      this.props.isRefillFetching ||
      this.props.isFetchingOpenOperation ||
      !this.props.accountsReady ||
      !this.props.dealsReady;

    const disabledRefillDemo =
      showRefillLoader ||
      parseFloat(activeAccount.balance) + openDealsSum >=
        this.props.minDemoRefillBalance;

    const refillDemoAvailable =
      this.props.tradeMode === "binary" &&
      activeAccount.type === "demo" &&
      !disabledRefillDemo;

    const refillTournamentAvailable =
      this.props.tradeMode === "tournament" &&
      !disableRefill({
        api: this.props.api
      });

    return (
      <Container>
        <Media query="(min-width: 960px)">
          {matches =>
            matches ? (
              <Section>
                <Logo />
              </Section>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: 5
                }}
              >
                <ToggleNav
                  active={this.state.isNavActive}
                  onClick={this.toggle}
                  isIOS={this.props.isIOS}
                  icon={<MenuIcon fontSize="inherit" color="inherit" />}
                  trade
                />
                <Section>
                  <StockLink
                    replace
                    to={
                      this.context != null
                        ? "/sightseeing/assets"
                        : "/trade/assets"
                    }
                  >
                    <StockInTools value={this.props.activeStock} />
                  </StockLink>
                </Section>
              </div>
            )
          }
        </Media>

        <Media
          query="(min-width: 960px)"
          render={() => (
            <Section center>
              <Tabs deals={this.props.deals} />
            </Section>
          )}
        />

        {"\n"}

        <Section separate>
          <Table>
            {profile ? (
              <ModalConsumer>
                {({ showModal }: { showModal: ShowModal }) => (
                  <TableCell>
                    <RatingExpanderProvider>
                      <AccountInHeader
                        profile={profile}
                        onDepositClick={this.onDepositClick}
                        accounts={accounts}
                        activeAccount={activeAccount}
                        onRefillDemoClick={this.props.onRefillDemoClick}
                        onProfileUpdate={this.props.onProfileUpdate}
                        refillDemoAvailable={refillDemoAvailable}
                        refillTournamentAvailable={refillTournamentAvailable}
                        onRefillTournamentClick={() => showModal(RefillPopup)}
                        showRefillLoader={showRefillLoader}
                        compensationUpdated={this.props.compensationUpdated}
                        compensationCreated={this.props.compensationCreated}
                      />
                    </RatingExpanderProvider>
                    {showDeposit === true && (
                      <DepositBonusBtn>
                        <Media query="(max-width: 479px)">
                          {matches =>
                            matches ? (
                              <Wallet width="17px" />
                            ) : (
                              <Gettext>Deposit</Gettext>
                            )
                          }
                        </Media>
                      </DepositBonusBtn>
                    )}
                  </TableCell>
                )}
              </ModalConsumer>
            ) : (
              <TableCell>
                <Link to="/login">
                  <Button type="deposit">
                    <Gettext>login</Gettext>
                  </Button>
                </Link>
              </TableCell>
            )}

            <Media
              query="(min-width: 960px)"
              render={() => (
                <TableCell>
                  <ToggleNav
                    active={this.state.isNavActive}
                    onClick={this.toggle}
                    isIOS={this.props.isIOS}
                    trade
                  />
                </TableCell>
              )}
            />
          </Table>
        </Section>

        {"\n"}

        <Navigation
          open={this.state.isNavActive}
          profile={profile}
          onClose={this.hide}
          trade={true}
          changeLocaleFetching={this.state.changeLocaleFetching}
          onLocaleChange={this.props.onLocaleChange}
          isVIP={isVIP}
        />
      </Container>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    activeStock: getViewedStock(state.tabs),
    tradeMode: state.settings.mode,
    currentBid: state.settings.currentBid,
    profile: state.profile,
    minFillup: state.remoteTradeSettings.minFillup,
    initialDeposit: state.remoteTradeSettings.initial,
    minDemoRefillBalance: state.remoteTradeSettings.minDemoRefillBalance,
    accounts: state.accounts,
    isAndroidApp: state.serverData ? state.serverData.isAndroidApp : false,
    isIOS: state.serverData ? state.serverData.isIOS : false,
    isVIP: state.serverData ? state.serverData.isVIP : false,
    appVersion: state.serverData ? state.serverData.app_version : false
  };
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToFetch
  )(
    withDeposit(
      withLanguage(props => (
        <Tournament.Consumer>
          {api => <Header {...props} api={api} />}
        </Tournament.Consumer>
      ))
    )
  )
);



// WEBPACK FOOTER //
// ./src/components/TradeHeader/Header.js